/**
 * The Alert_Rule list (route `/alert-rules`; Feature: granular-crud-api, tasks 14.1, 14.2;
 * Requirements 11.4, 11.6, 11.7, 11.8; Design: §11).
 *
 * `GET /v1/alert-rules` with the platform's `LoadState` shape, plus the row-level write affordances:
 * edit links and a delete confirmation. Three things are worth stating explicitly:
 *
 *  - **Origin badges.** A rule's provenance changes what a write to it *means*, so it is visible in the
 *    list rather than discovered inside a form (Req 6.9, 11.8).
 *  - **Deletion resolves Alerts.** Removing a rule closes every Alert firing for it, and the API returns
 *    how many it closed, so the confirmation says so and the result reports the count (Req 5.5).
 *  - **The list re-fetches only after a confirmed delete.** The rows are never mutated locally, so a
 *    failed delete leaves the pre-request view intact with the error in the dialog (Req 11.6). The
 *    re-fetch is driven by a reload counter in the effect's dependencies.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../api/client';
import type { AlertRuleDto } from '../api/types';
import { useApiClient } from '../api/use-api-client';
import { useCanWrite } from '../auth/use-can-write';
import { ProvenanceNotice, requiresAcknowledgement } from '../components/ProvenanceNotice';
import { OriginBadge } from '../components/Badges';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { TERRAFORM_SECTION_ID } from '../components/TerraformSection';
import { useWrite } from '../lib/use-write';

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string; isAuthError: boolean }
  | { status: 'loaded'; rules: AlertRuleDto[] };

/** A one-line summary of which Checks a rule covers. */
function describeSelector(rule: AlertRuleDto): string {
  const selector = rule.checkSelector;
  if (selector === undefined || selector.kind === 'all') return 'All checks';
  if (selector.kind === 'logicalName') {
    const names = selector.names ?? [];
    return names.length > 0 ? `Names: ${names.join(', ')}` : 'Names: —';
  }
  const ids = selector.checkIds ?? [];
  return ids.length > 0 ? `Ids: ${ids.join(', ')}` : 'Ids: —';
}

export function AlertRulesListPage(): JSX.Element {
  const client = useApiClient();
  const canWrite = useCanWrite();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pendingDelete, setPendingDelete] = useState<AlertRuleDto | undefined>(undefined);
  const [deleteNotice, setDeleteNotice] = useState<string | undefined>(undefined);
  /** The Terraform acknowledgement for the pending delete (Req 13.3), reset per dialog. */
  const [acknowledged, setAcknowledged] = useState(false);
  const write = useWrite();

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    client
      .listAlertRules()
      .then((response) => {
        if (active) setState({ status: 'loaded', rules: response.alertRules });
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message = error instanceof Error ? error.message : 'Failed to load alert rules';
        const isAuthError = error instanceof ApiError && error.isAuthError;
        setState({ status: 'error', message, isAuthError });
      });
    return () => {
      active = false;
    };
  }, [client, reloadToken]);

  function confirmDelete(rule: AlertRuleDto): void {
    void write.run(
      () => client.deleteAlertRule(rule.ruleId),
      (response) => {
        setPendingDelete(undefined);
        setDeleteNotice(
          response.resolvedAlerts > 0
            ? `Deleted the alert rule and resolved ${response.resolvedAlerts} firing alert(s).`
            : 'Deleted the alert rule.',
        );
        // Only a confirmed delete refreshes the list (Req 11.6).
        setReloadToken((token) => token + 1);
      },
    );
  }

  return (
    <section>
      <div className="section-header">
        <h1>Alert rules</h1>
        {canWrite ? <Link to="/alert-rules/new">New alert rule</Link> : null}
      </div>

      {deleteNotice !== undefined ? (
        <p role="status" className="muted">
          {deleteNotice}
        </p>
      ) : null}

      <AlertRulesBody
        state={state}
        canWrite={canWrite}
        onDelete={(rule) => {
          write.reset();
          setDeleteNotice(undefined);
          // A fresh decision per dialog: a tick left over from a rule the user backed out of must not
          // arm the delete for the next one, which may have a different origin entirely.
          setAcknowledged(false);
          setPendingDelete(rule);
        }}
      />

      {pendingDelete !== undefined ? (
        <ConfirmDialog
          title="Delete alert rule"
          message="Deleting this alert rule stops its evaluation and resolves every alert currently firing for it."
          confirmLabel="Delete alert rule"
          busy={write.state.submitting}
          errorMessage={write.state.errorMessage}
          confirmDisabled={requiresAcknowledgement(pendingDelete.origin) && !acknowledged}
          onConfirm={() => confirmDelete(pendingDelete)}
          onCancel={() => {
            write.reset();
            setPendingDelete(undefined);
          }}
        >
          <ProvenanceNotice
            origin={pendingDelete.origin}
            entity="alert rule"
            action="delete"
            acknowledged={acknowledged}
            onAcknowledge={setAcknowledged}
          />
        </ConfirmDialog>
      ) : null}
    </section>
  );
}

function AlertRulesBody({
  state,
  canWrite,
  onDelete,
}: {
  state: LoadState;
  canWrite: boolean;
  onDelete: (rule: AlertRuleDto) => void;
}): JSX.Element {
  if (state.status === 'loading') {
    return (
      <p role="status" className="muted">
        Loading alert rules…
      </p>
    );
  }

  if (state.status === 'error') {
    return (
      <div role="alert" className="error-panel">
        <p>
          {state.isAuthError ? 'Your session expired. Please sign in again.' : 'Could not load alert rules.'}
        </p>
        <p className="muted">{state.message}</p>
      </div>
    );
  }

  if (state.rules.length === 0) {
    return <p className="muted">No alert rules exist for this account yet.</p>;
  }

  return (
    <table className="checks-table" aria-label="Alert rules">
      <thead>
        <tr>
          <th scope="col">Applies to</th>
          <th scope="col">Fire on</th>
          <th scope="col">Severity</th>
          <th scope="col">Channels</th>
          <th scope="col">Managed by</th>
          <th scope="col">Terraform</th>
          {canWrite ? <th scope="col">Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {state.rules.map((rule) => (
          <tr key={rule.ruleId}>
            <td>{describeSelector(rule)}</td>
            <td>{rule.fireOn}</td>
            <td>{rule.severity}</td>
            <td>{rule.channels.length > 0 ? rule.channels.join(', ') : '—'}</td>
            <td>
              <OriginBadge origin={rule.origin} />
            </td>
            {/*
             * Requirement 14.2 — the import command and stanza are "reachable from the Alert_Rules list
             * row". A link to the edit page's `#terraform` anchor rather than the snippets inline: a
             * stanza is twenty lines of monospace and there is one per row.
             *
             * Its own always-present column, NOT the `canWrite` Actions cell. Adopting a rule into
             * Terraform is a read here — the writing happens in a repository, with an API key that has
             * nothing to do with this browser session — so withholding it from a viewer would hide
             * reference material rather than withhold a capability, and Requirement 14.6's second half
             * exists precisely for the reader who has a key but not an editor role in this Account.
             */}
            <td>
              <Link to={`/alert-rules/${encodeURIComponent(rule.ruleId)}/edit#${TERRAFORM_SECTION_ID}`}>
                Terraform
                <span className="visually-hidden"> configuration for alert rule {rule.ruleId}</span>
              </Link>
            </td>
            {canWrite ? (
              <td className="row-actions">
                <Link to={`/alert-rules/${encodeURIComponent(rule.ruleId)}/edit`}>Edit</Link>
                <button
                  type="button"
                  className="danger"
                  aria-label={`Delete alert rule ${rule.ruleId}`}
                  onClick={() => onDelete(rule)}
                >
                  Delete
                </button>
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
